/**
 * Type Contracts for Common Application Layout
 * Feature: 002-make-a-common
 * 
 * This file defines the TypeScript interfaces and types for the layout system.
 * These contracts ensure type safety and consistency across all layout components.
 */

import { LucideIcon } from 'lucide-react'

// ============================================================================
// Navigation Types
// ============================================================================

/**
 * Represents a single navigation menu item in the sidebar
 */
export interface NavigationItem {
  /** Unique identifier for the navigation item (typically matches route path) */
  id: string
  
  /** Display label for the navigation item */
  label: string
  
  /** Lucide icon component to display */
  icon: LucideIcon
  
  /** Route path to navigate to */
  path: string
  
  /** Optional badge count to display (e.g., unread notifications) */
  badge?: number
  
  /** Optional nested navigation items (max 1 level deep) */
  children?: NavigationItem[]
  
  /** Optional permissions required to view this item */
  requiredPermissions?: string[]
}

/**
 * Navigation configuration for the entire application
 */
export interface NavigationConfig {
  /** Main navigation items displayed in sidebar */
  items: NavigationItem[]
}

// ============================================================================
// Breadcrumb Types
// ============================================================================

/**
 * Represents a single segment in the breadcrumb trail
 */
export interface BreadcrumbSegment {
  /** Display label for the breadcrumb segment */
  label: string
  
  /** Route path this segment links to */
  path: string
  
  /** Whether this is the current/active page */
  isActive: boolean
}

/**
 * Route handle configuration for breadcrumb generation
 * Add this to route definitions in React Router
 */
export interface RouteHandle {
  /** Breadcrumb label for this route */
  breadcrumb?: string | ((params: Record<string, string>) => string)
  
  /** Page title for this route */
  title?: string | ((params: Record<string, string>) => string)
  
  /** Whether to hide this route from breadcrumbs */
  hideBreadcrumb?: boolean
}

// ============================================================================
// User Profile Types
// ============================================================================

/**
 * Represents the authenticated user displayed in the top bar
 */
export interface UserProfile {
  /** Unique user identifier */
  id: string
  
  /** User's full display name */
  name: string
  
  /** User's email address */
  email?: string
  
  /** URL to user's avatar image (falls back to initials if undefined) */
  avatar?: string
  
  /** User's role or title */
  role?: string
}

/**
 * User menu item displayed in the avatar dropdown
 */
export interface UserMenuItem {
  /** Unique identifier for the menu item */
  id: string
  
  /** Display label */
  label: string
  
  /** Icon to display */
  icon: LucideIcon
  
  /** Click handler or route path */
  action: string | (() => void)
  
  /** Visual variant (default or danger for logout) */
  variant?: 'default' | 'danger'
}

// ============================================================================
// Notification Types
// ============================================================================

/**
 * Notification severity levels
 */
export type NotificationType = 'info' | 'success' | 'warning' | 'error'

/**
 * Represents a single notification item
 */
export interface Notification {
  /** Unique notification identifier */
  id: string
  
  /** Notification headline */
  title: string
  
  /** Notification body text */
  message: string
  
  /** When the notification was created */
  timestamp: Date
  
  /** Whether the user has read this notification */
  read: boolean
  
  /** Notification severity type */
  type: NotificationType
  
  /** Optional link to related page */
  actionUrl?: string
}

// ============================================================================
// Layout State Types
// ============================================================================

/**
 * Layout UI state managed by LayoutContext
 */
export interface LayoutState {
  /** Whether the sidebar is collapsed (icon-only mode) */
  sidebarCollapsed: boolean
  
  /** Whether the mobile menu overlay is open */
  mobileMenuOpen: boolean
  
  /** Current active route path */
  currentRoute: string
}

/**
 * Layout actions for state updates
 */
export interface LayoutActions {
  /** Toggle sidebar collapsed/expanded state */
  toggleSidebar: () => void
  
  /** Set sidebar collapsed state explicitly */
  setSidebarCollapsed: (collapsed: boolean) => void
  
  /** Toggle mobile menu open/closed */
  toggleMobileMenu: () => void
  
  /** Set mobile menu state explicitly */
  setMobileMenuOpen: (open: boolean) => void
  
  /** Update current route (called automatically on navigation) */
  setCurrentRoute: (route: string) => void
}

/**
 * Complete layout context value (state + actions)
 */
export interface LayoutContextValue extends LayoutState, LayoutActions {}

// ============================================================================
// Component Props Types
// ============================================================================

/**
 * Props for MainLayout component
 */
export interface MainLayoutProps {
  /** Page content to render in the main content area */
  children: React.ReactNode
  
  /** Optional className for custom styling */
  className?: string
}

/**
 * Props for Sidebar component
 */
export interface SidebarProps {
  /** Navigation items to display */
  navigationItems: NavigationItem[]
  
  /** Current active route path */
  currentPath: string
  
  /** Whether sidebar is collapsed */
  collapsed: boolean
  
  /** Whether mobile menu is open */
  mobileMenuOpen: boolean
  
  /** Callback when collapse button is clicked */
  onToggleCollapse: () => void
  
  /** Callback when mobile menu should close */
  onCloseMobileMenu: () => void
}

/**
 * Props for TopBar component
 */
export interface TopBarProps {
  /** Breadcrumb segments to display */
  breadcrumbs: BreadcrumbSegment[]
  
  /** Current page title */
  pageTitle: string
  
  /** User profile information */
  user: UserProfile
  
  /** Notifications to display */
  notifications: Notification[]
  
  /** Whether sidebar is collapsed (affects layout) */
  sidebarCollapsed: boolean
  
  /** Callback when mobile menu toggle is clicked */
  onToggleMobileMenu: () => void
}

/**
 * Props for Breadcrumb component
 */
export interface BreadcrumbProps {
  /** Breadcrumb segments to render */
  segments: BreadcrumbSegment[]
  
  /** Optional className for custom styling */
  className?: string
}

/**
 * Props for UserMenu component
 */
export interface UserMenuProps {
  /** User profile to display */
  user: UserProfile
  
  /** Menu items to show in dropdown */
  menuItems: UserMenuItem[]
}

/**
 * Props for NotificationBell component
 */
export interface NotificationBellProps {
  /** Notifications to display */
  notifications: Notification[]
  
  /** Callback when notification is clicked */
  onNotificationClick?: (notification: Notification) => void
  
  /** Callback when "View All" is clicked */
  onViewAll?: () => void
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * SessionStorage keys for layout persistence
 */
export const LAYOUT_STORAGE_KEYS = {
  SIDEBAR_COLLAPSED: 'layout:sidebarCollapsed',
} as const

/**
 * Responsive breakpoints (matches TailwindCSS)
 */
export const BREAKPOINTS = {
  MOBILE: 768,    // < 768px
  TABLET: 1024,   // 768px - 1024px
  DESKTOP: 1024,  // >= 1024px
} as const

/**
 * Z-index scale for layout layers
 */
export const Z_INDEX = {
  CONTENT: 0,
  TOPBAR: 30,
  MOBILE_BACKDROP: 40,
  SIDEBAR: 50,
  MOBILE_OVERLAY: 60,
} as const

/**
 * Type guard to check if a value is a NavigationItem
 */
export function isNavigationItem(value: unknown): value is NavigationItem {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    'label' in value &&
    'path' in value &&
    typeof (value as NavigationItem).id === 'string' &&
    typeof (value as NavigationItem).label === 'string' &&
    typeof (value as NavigationItem).path === 'string'
  )
}

/**
 * Type guard to check if a value is a BreadcrumbSegment
 */
export function isBreadcrumbSegment(value: unknown): value is BreadcrumbSegment {
  return (
    typeof value === 'object' &&
    value !== null &&
    'label' in value &&
    'path' in value &&
    'isActive' in value &&
    typeof (value as BreadcrumbSegment).label === 'string' &&
    typeof (value as BreadcrumbSegment).path === 'string' &&
    typeof (value as BreadcrumbSegment).isActive === 'boolean'
  )
}
